import React from 'react';
import { View } from 'react-native';
import { Text, useTheme } from '../../../ui';

const show = (v: unknown) => (v === undefined ? '—' : v === null ? 'null' : typeof v === 'string' ? v : JSON.stringify(v, null, 1));
const same = (a: unknown, b: unknown) => JSON.stringify(a) === JSON.stringify(b);

/** Field-by-field before/after comparison of two JSON objects. */
export function JsonDiff({ before, after, showUnchanged }: { before: Record<string, unknown> | null; after: Record<string, unknown> | null; showUnchanged?: boolean }) {
  const { colors } = useTheme();
  const keys = Array.from(new Set([...Object.keys(before ?? {}), ...Object.keys(after ?? {})])).sort();
  const rows = keys.filter((k) => showUnchanged || !same(before?.[k], after?.[k]));
  if (!keys.length) return <Text variant="muted">No field data recorded.</Text>;
  if (!rows.length) return <Text variant="muted">No fields changed.</Text>;
  return (
    <View style={{ borderWidth: 1, borderColor: colors.border, borderRadius: 10, overflow: 'hidden' }} testID="json-diff">
      <View style={{ flexDirection: 'row', backgroundColor: colors.surface2, borderBottomWidth: 1, borderBottomColor: colors.border }}>
        {['Field', 'Before', 'After'].map((h, i) => (
          <View key={h} style={{ flex: i === 0 ? 0.8 : 1, padding: 8 }}>
            <Text variant="caption">{h}</Text>
          </View>
        ))}
      </View>
      {rows.map((k, i) => {
        const changed = !same(before?.[k], after?.[k]);
        const added = before == null || !(k in before);
        const removed = after == null || !(k in after);
        return (
          <View key={k} style={{ flexDirection: 'row', borderBottomWidth: i < rows.length - 1 ? 1 : 0, borderBottomColor: colors.border }}>
            <View style={{ flex: 0.8, padding: 8 }}>
              <Text variant="mono" size={12} weight={changed ? 'semibold' : 'regular'}>
                {k}
              </Text>
            </View>
            <View style={{ flex: 1, padding: 8, backgroundColor: changed && !added ? colors.dangerSoft : 'transparent' }}>
              <Text variant="mono" size={12} color={changed && !added ? colors.danger : colors.muted} selectable>
                {show(before?.[k])}
              </Text>
            </View>
            <View style={{ flex: 1, padding: 8, backgroundColor: changed && !removed ? colors.successSoft : 'transparent' }}>
              <Text variant="mono" size={12} color={changed && !removed ? colors.success : colors.muted} selectable>
                {show(after?.[k])}
              </Text>
            </View>
          </View>
        );
      })}
    </View>
  );
}
